<template>
  <div
    class="map-wrapper"
    :class="{
      'map-wrapper--route-visible': isRouteVisible,
      'map-wrapper--multicolumn': isMapVisible && isRouteVisible,
    }"
  >
    <div v-if="isRouteVisible" class="location-points">
      <div class="location-points__inner">
        <div class="location-points__title">
          {{ t('product.show.map.title') }}
        </div>
        <div class="location-points__list markers">
          <template v-for="(point, i) in points" :key="point.lat + point.lng">
            <div
              v-show="i + 1 < 4 || isListOpen"
              class="location-points__item marker"
              :class="{
                'location-points__item--hidden': i + 1 > 3 && !isListOpen,
                'location-points__item--hover': isMapVisible,
              }"
            >
              <div v-if="point.name" class="text-base font-semibold">
                {{ i + 1 }}. {{ point.name }}
              </div>
              <div v-if="point.description" class="color-[#525252] text-xs">
                {{ point.description }}
              </div>
            </div>
          </template>
        </div>

        <div
          v-if="points.length > 3 && !isListOpen"
          class="show-more"
          @click="isListOpen = true"
        >
          {{ t('product.show.map.showMore') }}
        </div>
      </div>
    </div>

    <div
      v-if="isMapVisible"
      id="map"
      ref="mapRef"
      class="itinerary-map-source"
    ></div>
  </div>
</template>

<script setup lang="ts">
import { onMounted } from 'vue'

const { t } = useI18n()
const mapRef = ref(null)<HTMLElement>
const isListOpen = ref(false)<boolean>

const props = withDefaults(
  defineProps<{
    points: [{ lat: number; lng: number; name?: string; description?: string }]
    isRouteVisible: boolean
    isMapVisible: boolean
  }>(),
  {
    points: () => [],
    isRouteVisible: false,
    isMapVisible: true,
  }
)

const leafletMarkers = ref<HTMLElement[]>([])

const map = ref(null)
const leaflet = ref(null)

const markerEls = ref([]) // ref to markers
const listeners = ref([]) // ref to listeners

const isSinglePin = computed(() => {
  return props.points.length === 1
})

onMounted(async () => {
  if (!props.isMapVisible) return
  leaflet.value = await import('leaflet')

  // set single class if only 1 waypoint
  const pinClassName = 'marker-number' + (isSinglePin.value ? ' single' : '')

  // map init
  map.value = leaflet.value.map(mapRef.value)

  leaflet.value
    .tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: false,
    })
    .addTo(map.value)

  // get list of coordinates from props
  const latLngs = props.points.map((item) => L.latLng(item.lat, item.lng))

  // polyline is drawing lines between provided coordinates
  L.polyline(latLngs, {
    color: '#5E7505',
    weight: 4,
    opacity: 1,
    dashArray: '8, 8',
  }).addTo(map.value)

  // add markers for each point
  latLngs.forEach((point, i) => {
    // marker
    const marker = L.marker(point, {
      icon: L.divIcon({
        className: 'custom-marker',
        html: `<div class="${pinClassName} marker-${i + 1}">${!isSinglePin.value ? i + 1 : ''}</div>`,
        iconSize: [20, 26],
        iconAnchor: [10, 13],
      }),
    }).addTo(map.value)

    // after marker was added on map, collect element to array
    marker.on('add', () => {
      const el = marker.getElement()
      if (el) {
        leafletMarkers.value[i] = el
      }
    })
  })

  // setting zoom
  if (props.points.length === 1) {
    // zoom when we have only single point
    map.value.setView([props.points[0].lat, props.points[0].lng], 13)
  } else {
    // when we have multiple coordinates, set zoom to fit all
    const bounds = leaflet.value.latLngBounds(
      props.points.map((point) => [point.lat, point.lng])
    )

    map.value.fitBounds(bounds, {
      padding: [50, 50],
    })
  }

  // eventListeners on markers when hover external elements
  nextTick(() => {
    // collect all markers
    markerEls.value = Array.from(document.querySelectorAll('.markers .marker'))

    markerEls.value.forEach((el, i) => {
      // mouseenter listener
      const onMouseEnter = () => {
        leafletMarkers.value[i]?.classList.add('hover')
      }

      // mouseleave listener
      const onMouseLeave = () => {
        leafletMarkers.value[i]?.classList.remove('hover')
      }

      // click listener
      const onClick = () => {
        const point = props.points[i]
        if (map.value && point) {
          map.value.setView([point.lat, point.lng], 16, {
            animate: true,
          })
        }
      }

      // add all listeners
      el.addEventListener('mouseenter', onMouseEnter)
      el.addEventListener('mouseleave', onMouseLeave)
      el.addEventListener('click', onClick)

      // add all listeners to array
      listeners.value.push({ el, onMouseEnter, onMouseLeave, onClick })
    })
  })
})

onUnmounted(() => {
  if (map.value) {
    map.value.remove()
    map.value = null
  }

  listeners.value.forEach(({ el, onMouseEnter, onMouseLeave, onClick }) => {
    el.removeEventListener('mouseenter', onMouseEnter)
    el.removeEventListener('mouseleave', onMouseLeave)
    el.removeEventListener('click', onClick)
  })

  markerEls.value = []
  listeners.value = []
})
</script>

<style lang="postcss">
@import url('/node_modules/leaflet/dist/leaflet.css');

.custom-marker {
  .marker-number {
    position: relative;
    display: block;
    width: 100%;
    height: 100%;
    background: url('/_nuxt3-static/map-pin.svg') center no-repeat;
    text-align: center;
    color: #fff;
    font-size: 12px;
    font-weight: 600;
    padding: 2px 0 0;
    transition: 300ms all ease;

    &:hover {
      transform: scale(1.6);
      @apply text-lime;
    }
  }

  &.hover .marker-number {
    transform: scale(1.6);
    @apply text-lime;
  }
}

.marker-number {
  color: #fff;
  font-size: 12px;
  font-weight: 500;
  width: 20px;
  height: 23px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
}
</style>

<style lang="postcss" scoped>
.map-wrapper {
  @apply w-full;

  &--route-visible {
    @apply md:py-[40px] md:px-[20px];
  }

  &--multicolumn {
    @apply flex flex-col md:flex-row gap-6;

    .location-points {
      @apply max-w-[503px];
    }

    .itinerary-map-source {
      @apply md:w-[calc(100%-503px-24px)] order-1 md:order-2;
    }
  }
}

.itinerary-map-source {
  @apply relative block h-[300px] md:h-[421px] rounded-lg w-full;
}

.location-points {
  @apply w-full flex items-center order-2 md:order-1;

  &__title {
    @apply text-h5 mb-4;
  }

  &__inner {
    @apply w-full;
  }

  &__list {
    @apply flex flex-col gap-[10px] pl-[27px] relative;

    &::before {
      content: '';
      @apply absolute left-[10px] top-0 bottom-0 w-[2px] bg-repeat-y bg-center;
      background-image: url('/_nuxt3-static/events/route-lines.svg');
    }
  }

  &__item {
    @apply border border-[#f9f7f5] rounded-[8px] p-[14px_16px] shadow-[0_1px_4px_0_rgba(0,0,0,0.05)] bg-white relative;

    &--hover {
      @apply cursor-pointer transition-all duration-300 ease-in-out hover:shadow-[0_1px_4px_0_rgba(0,0,0,0.15)];
    }

    &::before {
      content: '';
      @apply absolute left-[-26px] top-0 bottom-0 my-auto w-[18px] h-[18px] shadow-[0_1px_4px_0_rgba(0,0,0,0.05)] bg-[#f1ffb5] border-[5px] border-[#cced3a] rounded-full z-[1];
    }

    &:has(+ .location-points__item--hidden),
    &:last-child,
    &:first-child {
      &::after {
        content: '';
        @apply absolute left-[-18px] w-[2px] top-[-1px] bottom-1/2 bg-[#fcfbfa];
      }
    }

    &:last-child,
    &:has(+ .location-points__item--hidden) {
      &::after {
        @apply top-1/2 bottom-0;
      }
    }
  }
}

.show-more {
  @apply text-base font-semibold mt-3 ml-[26px] underline text-[#08373b] cursor-pointer hover:no-underline;
}
</style>
